import { useEffect, useState } from 'react';

type Props = {
  label: string;
  value: number | null;
  max?: number;
  disabled?: boolean;
  /** The value is inherited, not set here: shown muted, with `inheritedHint` as its tooltip. */
  inherited?: boolean;
  inheritedHint?: string;
  /** Called on Enter or blur when the typed number differs from `value`; resolving to false (not saved) restores `value`. */
  onCommit: (value: number) => void | Promise<boolean | void>;
};

/**
 * Always-visible percent input: partners missed the hover-only pencil, so the
 * field looks editable at all times and saves on Enter or when focus leaves.
 */
export function PercentInput({ label, value, max, disabled, inherited, inheritedHint, onCommit }: Props) {
  const [draft, setDraft] = useState(value != null ? String(value) : '');
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setDraft(value != null ? String(value) : '');
    setInvalid(false);
  }, [value]);

  function commit() {
    const raw = draft.trim();
    if (raw === '') {
      setDraft(value != null ? String(value) : '');
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next) || next < 0 || (max != null && next > max)) {
      setInvalid(true);
      return;
    }
    if (next === value) return;
    void Promise.resolve(onCommit(next)).then((saved) => {
      if (saved === false) setDraft(value != null ? String(value) : '');
    });
  }

  return (
    <span
      title={inherited ? inheritedHint : undefined}
      className={`inline-flex items-center rounded border bg-[var(--co-bg)] pr-2 focus-within:border-[var(--co-brand)] ${
        invalid ? 'border-[var(--co-error)]' : 'border-[var(--co-border)]'
      }`}
    >
      <input
        type="number"
        inputMode="decimal"
        min={0}
        max={max}
        step="0.01"
        value={draft}
        disabled={disabled}
        aria-label={label}
        aria-invalid={invalid}
        onChange={(e) => {
          setDraft(e.target.value);
          setInvalid(false);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(value != null ? String(value) : '');
            setInvalid(false);
          }
        }}
        className={`w-16 bg-transparent px-2 py-1 text-right font-mono text-[13px] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
          inherited ? 'text-[var(--co-muted)] focus:text-[var(--co-text)]' : 'text-[var(--co-text)]'
        }`}
      />
      <span className="text-[12px] text-[var(--co-muted)]">%</span>
    </span>
  );
}
